import React, { useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import AuthContext from 'context/authContext';
import { useBranches } from 'hooks/useBranches';

const BranchDropdown = () => {
  const {
    user,
    selectedBranchId,
    selectAdminBranch,
    selectBranch,
    isOrganizationAdmin
  } = useContext(AuthContext);

  // Org admins can switch across every branch in the organisation.
  const { branches, isLoading, refetch } = useBranches();

  // Branch users (non-org-admin) can switch only between the branches they're
  // actually a member of. Each membership is a users_roles row carrying its own
  // role/permissions, which we apply on switch via selectBranch.
  const membershipBranchesMap = new Map();
  (user?.users_roles || [])
    .filter(role => role.is_active && role.branch_id && role.branch)
    .forEach(role => {
      if (!membershipBranchesMap.has(role.branch_id)) {
        membershipBranchesMap.set(role.branch_id, role);
      }
    });
  const membershipRoles = Array.from(membershipBranchesMap.values());

  const handleDropdownToggle = isOpen => {
    if (isOpen && isOrganizationAdmin) {
      // Refetch branches when dropdown opens to get latest data
      refetch();
    }
  };

  /* ----------------------------- Org admin view ---------------------------- */
  if (isOrganizationAdmin) {
    if (isLoading || !branches?.length) {
      return null;
    }

    const currentBranch =
      selectedBranchId === 'all'
        ? { name: 'All Branches' }
        : branches.find(b => b.id === selectedBranchId);

    const handleBranchSelect = branch => {
      selectAdminBranch({
        selectedBranchId: branch.id,
        is_main: branch.is_main,
        isAllBranches: branch.id === 'all'
      });

      // Force page refresh to reinitialize with new branch data
      window.location.reload();
    };

    return (
      <div className="d-flex align-items-center ms-3">
        <Dropdown onToggle={handleDropdownToggle}>
          <Dropdown.Toggle
            variant="link"
            className="text-decoration-none text-700 d-flex align-items-center"
            id="branch-dropdown"
          >
            <FontAwesomeIcon icon={faBuilding} className="me-2" />
            <span className="fw-medium" style={{ fontSize: '1rem' }}>
              {currentBranch?.name || 'Select Branch'}
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu className="dropdown-menu-end py-0">
            <div className="bg-white dark__bg-1000 rounded-2 py-2">
              <h6 className="dropdown-header" style={{ fontSize: '1rem' }}>
                Select Branch
              </h6>
              <Dropdown.Divider className="my-1" />
              {/* All Branches option for admin users */}
              <Dropdown.Item
                className={`d-flex align-items-center ${
                  selectedBranchId === 'all'
                    ? 'bg-soft-primary text-primary'
                    : ''
                }`}
                onClick={() =>
                  handleBranchSelect({
                    id: 'all',
                    name: 'All Branches',
                    is_main: false
                  })
                }
              >
                <div>
                  <div className="fw-medium" style={{ fontSize: '1rem' }}>
                    All Branches
                  </div>
                  <div className="text-500" style={{ fontSize: '0.875rem' }}>
                    View all organization data
                  </div>
                </div>
              </Dropdown.Item>
              <Dropdown.Divider className="my-1" />
              {branches.map(branch => (
                <Dropdown.Item
                  key={branch.id}
                  className={`d-flex align-items-center ${
                    branch.id === selectedBranchId
                      ? 'bg-soft-primary text-primary'
                      : ''
                  }`}
                  onClick={() => handleBranchSelect(branch)}
                  disabled={!branch.is_active}
                >
                  <div>
                    <div className="fw-medium" style={{ fontSize: '1rem' }}>
                      {branch.name}
                    </div>
                    {branch.is_main && (
                      <div
                        className="text-500"
                        style={{ fontSize: '0.875rem' }}
                      >
                        Main Branch
                      </div>
                    )}
                    {!branch.is_active && (
                      <div
                        className="text-danger"
                        style={{ fontSize: '0.875rem' }}
                      >
                        Inactive
                      </div>
                    )}
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  /* --------------------------- Branch user view ---------------------------- */
  // Only show a switcher when the user belongs to more than one branch.
  if (membershipRoles.length <= 1) {
    return null;
  }

  const currentMembership = membershipRoles.find(
    role => role.branch_id === selectedBranchId
  );

  const handleMembershipSelect = role => {
    // selectBranch applies the role/permissions of the chosen membership and
    // reads is_main from the passed userRole, so normalise it onto the object.
    selectBranch({
      userRole: { ...role, is_main: role.branch?.is_main },
      is_main: role.branch?.is_main
    });

    // Force page refresh to reinitialize with the newly selected branch.
    window.location.reload();
  };

  return (
    <div className="d-flex align-items-center ms-3">
      <Dropdown>
        <Dropdown.Toggle
          variant="link"
          className="text-decoration-none text-700 d-flex align-items-center"
          id="branch-dropdown"
        >
          <FontAwesomeIcon icon={faBuilding} className="me-2" />
          <span className="fw-medium" style={{ fontSize: '1rem' }}>
            {currentMembership?.branch?.name || 'Select Branch'}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-menu-end py-0">
          <div className="bg-white dark__bg-1000 rounded-2 py-2">
            <h6 className="dropdown-header" style={{ fontSize: '1rem' }}>
              Select Branch
            </h6>
            <Dropdown.Divider className="my-1" />
            {membershipRoles.map(role => (
              <Dropdown.Item
                key={role.branch_id}
                className={`d-flex align-items-center ${
                  role.branch_id === selectedBranchId
                    ? 'bg-soft-primary text-primary'
                    : ''
                }`}
                onClick={() => handleMembershipSelect(role)}
              >
                <div>
                  <div className="fw-medium" style={{ fontSize: '1rem' }}>
                    {role.branch?.name}
                  </div>
                  {role.branch?.is_main && (
                    <div className="text-500" style={{ fontSize: '0.875rem' }}>
                      Main Branch
                    </div>
                  )}
                </div>
              </Dropdown.Item>
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default BranchDropdown;
